import './styles/app.scss';

import { Route, Switch, useHistory, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { BasketProvider } from './contexts/BasketContext';
import { AlertProvider } from './contexts/AlertContext';
import { OrderProvider } from './contexts/OrderContext';
import { ModalProvider } from './contexts/ModalContext';
import { FiltersProvider } from './contexts/FiltersContext';
import gtmPageView from './helpers/gtmPageView';
import ViewYourStone from './routes/ViewYourStone';
import SwitchBrowser from './routes/SwitchBrowser';
import Footer from './components/Footer';
import error404 from './routes/error404';
import Nav from './components/Nav';
import Alert from './components/Alert';
import CertificateDownload from './routes/CertificateDownload';
import axios from 'axios';
import getDomain from './helpers/getDomain';

function App() {
  const [gtmId] = useState('GTM-MLHXXS9');
  const history = useHistory();
  const location = useLocation();

  useEffect(() =>
  {
    axios
      .get(getDomain() + '/api/config')
      .then((response) =>
      {
        if (response?.data?.oneTrustId)
        {
          //create one trust script
          const mainScript = document.createElement('script');
          mainScript.src = "https://cdn-ukwest.onetrust.com/scripttemplates/otSDKStub.js";
          mainScript.setAttribute("data-domain-script", response.data.oneTrustId);

          //find script containing the function
          const functionScript = document.getElementById("one-trust");

          //insert one before the other
          document.head.insertBefore(mainScript, functionScript);
        }

        gtmPageView(window.location);
      })
      .catch((error) =>
      {
        if (error.response)
        {
          console.error(error.response.data);
        }
        else if (error.request)
        {
          console.error(error.request);
        }
        else
        {
          console.error(error.message);
        }
      });
  }, [gtmId]);

  useEffect(() => {
    return history.listen((location) => {
      gtmPageView(location);
      window.scrollTo(0, 0);
    });
  }, [history]);

  useEffect(
    () => {
      // Safari detection
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent)
      const isDesktop = () => {
        const navigatorAgent = navigator.userAgent || navigator.vendor || window.opera;

        const desktop = !(
          /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ipad|ip(hone|od|ad)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series([46])0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino|android|ipad|playbook|silk/i.test(
            navigatorAgent
          ) ||
          /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br([ev])w|bumb|bw-([nu])|c55\/|capi|ccwa|cdm-|cell|chtm|cldc|cmd-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc-s|devi|dica|dmob|do([cp])o|ds(12|-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly([-_])|g1 u|g560|gene|gf-5|g-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd-([mpt])|hei-|hi(pt|ta)|hp( i|ip)|hs-c|ht(c([- _agpst])|tp)|hu(aw|tc)|i-(20|go|ma)|i230|iac([ \-/])|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja([tv])a|jbro|jemu|jigs|kddi|keji|kgt([ /])|klon|kpt |kwc-|kyo([ck])|le(no|xi)|lg( g|\/([klu])|50|54|-[a-w])|libw|lynx|m1-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t([- ov])|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30([02])|n50([025])|n7(0([01])|10)|ne(([cm])-|on|tf|wf|wg|wt)|nok([6i])|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan([adt])|pdxg|pg(13|-([1-8]|c))|phil|pire|pl(ay|uc)|pn-2|po(ck|rt|se)|prox|psio|pt-g|qa-a|qc(07|12|21|32|60|-[2-7]|i-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h-|oo|p-)|sdk\/|se(c([-01])|47|mc|nd|ri)|sgh-|shar|sie([-m])|sk-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h-|v-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl-|tdg-|tel([im])|tim-|t-mo|to(pl|sh)|ts(70|m-|m3|m5)|tx-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c([- ])|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas-|your|zeto|zte-/i.test(
            navigatorAgent.substr(0, 4)
          )
        )
        const notIPad = !(window.matchMedia("(pointer: coarse)").matches)

        return (desktop && notIPad) ? true : false
      };
      
      const path = location.pathname
      if (path !== '/switch-browser' && isSafari && isDesktop()) {
        history.push("/switch-browser")
      }
    },
    [location, history]
  )

  return (
    <AlertProvider>
      <BasketProvider>
        <OrderProvider>
          <ModalProvider>
            <FiltersProvider>
              <Nav />
              <Alert />
              <Switch>

                <Route
                  path="/"
                  exact
                  component={() =>
                  {
                    let components = [<ViewYourStone prefix="s" />, <ViewYourStone prefix="e" />, <ViewYourStone prefix="n" />, <ViewYourStone prefix="w" />];
                    return components[Math.floor(Math.random() * components.length)];
                  }}
                />
                
                <Route
                  path="/test"
                  component={() => <ViewYourStone prefix="n" />}
                />

                <Route
                  path="/north"
                  component={() => <ViewYourStone prefix="n" />}
                />

                <Route
                  path="/east"
                  component={() => <ViewYourStone prefix="e" />}
                />

                <Route
                  path="/south"
                  component={() => <ViewYourStone prefix="s" />}
                />

                <Route
                  path="/west"
                  component={() => <ViewYourStone prefix="w" />}
                />

                <Route
                  path="/id/:stoneId"
                  component={ViewYourStone}
                />

                <Route
                  path="/download/:orderId/:stoneId"
                  component={CertificateDownload}
                />

                <Route
                  path="/switch-browser"
                  component={SwitchBrowser}
                />

                <Route component={error404} />

              </Switch>
              <Footer />
            </FiltersProvider>
          </ModalProvider>
        </OrderProvider>
      </BasketProvider>
    </AlertProvider>
  );
}

export default App;
